/* eslint-disable camelcase */
import InstagramPluginModerator from '@/models/plugin/instagramPluginModerator';
import { formatDateTime } from '@/utils/format';

export interface BasePluginData {
  _id: string;
  name: string;
  owner_uuid: string;
  admins_uuid: string[];
  type: 'instagram' | 'facebook';
  version: string;
  created_at: string;
  updated_at: string;
  status: string;
  moderators: any[];
  profile_picture_base64: string;
  is_owner: boolean;
  live_media?: string;
  pending_full_sync_media_id?: string;
  last_seen_live?: string;
  settings?: {
    auto_message_enabled?: boolean;
  };
}

export abstract class BasePlugin {
  id: string;

  name: string;

  ownerUuid: string;

  adminsUuid: string[];

  type: 'instagram' | 'facebook';

  version: string;

  createdAt: Date;

  updatedAt: Date;

  status: string;

  moderators: InstagramPluginModerator[];

  profilePictureBase64: string;

  isOwner: boolean;

  liveMedia: string;

  pendingFullSyncMediaId: string;

  lastSeenLive: Date | null;

  autoMessageEnabled: boolean;

  constructor(data: BasePluginData) {
    // eslint-disable-next-line no-underscore-dangle
    this.id = data._id;
    this.name = data.name;
    this.ownerUuid = data.owner_uuid;
    this.adminsUuid = data.admins_uuid || [];
    this.type = data.type;
    this.version = data.version;
    this.createdAt = new Date(data.created_at);
    this.updatedAt = new Date(data.updated_at);
    this.status = data.status || '';
    this.moderators = data.moderators
      ? data.moderators.map((m: any) => new InstagramPluginModerator(m))
      : [];
    this.profilePictureBase64 = data.profile_picture_base64 || '';
    this.isOwner = data.is_owner || false;
    this.liveMedia = data.live_media || '';
    this.pendingFullSyncMediaId = data.pending_full_sync_media_id || '';
    this.lastSeenLive = data.last_seen_live ? new Date(data.last_seen_live) : null;
    // Default true — matches pre-toggle behaviour where every plugin had automation on.
    this.autoMessageEnabled = data.settings?.auto_message_enabled ?? true;
  }

  hasUnprocessedLive(): boolean {
    return this.pendingFullSyncMediaId !== '';
  }

  /** True when the Instagram connection expired and the seller must
   *  re-authorise. Mirrors `Plugin.needsReconnect()` so the integrations
   *  list, the dashboard card and the connection banner cannot disagree.
   *  Comments still arrive over the webhook while this is true — what
   *  stops is automated replies, live detection and sync. */
  needsReconnect(): boolean {
    return this.type === 'instagram' && this.status === 'token_refresh_failed';
  }

  isInstagram(): boolean {
    return this.type === 'instagram';
  }

  isFacebook(): boolean {
    return this.type === 'facebook';
  }

  isOldVersion(): boolean {
    return !this.version;
  }

  formattedCreatedAt(): string {
    return formatDateTime(this.createdAt);
  }

  formattedUpdatedAt(): string {
    return formatDateTime(this.updatedAt);
  }
}

export default BasePlugin;
